import { motion } from "framer-motion";
import { Wrench, FileText, Scale } from "lucide-react";
import Watermark from "@/components/landing/Watermark";
import HeroCarousel from "@/components/landing/HeroCarousel";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const valueCards = [
  {
    icon: Wrench,
    title: "Supervisión Técnica y Gestión Proactiva",
    text: "Presencia periódica en el inmueble, control directo de instalaciones y coordinación ágil con proveedores. Nos anticipamos a los problemas para cuidar el edificio antes de que surja la avería.",
  },
  {
    icon: FileText,
    title: "Cuentas Claras y Acceso Digital",
    text: "Consulta permanente de facturas, actas y estado de avisos desde el portal de propietarios. Cada euro de la comunidad con justificante y total transparencia.",
  },
  {
    icon: Scale,
    title: "Mediación Extrajudicial y Rigor Normativo",
    text: "Prevención y resolución de discrepancias mediante acuerdos documentados de buena fe. Seguridad en acuerdos de junta y protección de la convivencia vecinal.",
  },
];

export default function Hero() {
  return (
    <section id="inicio" data-testid="hero-section" className="relative pt-32 sm:pt-28 lg:pt-40 pb-8 sm:pb-[clamp(2rem,3.5vw,3rem)] lg:pb-20">
      <Watermark />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-10 items-start">
          <div>
            <h1
              data-testid="hero-title"
              className="font-display section-title text-slate-900 lg:[hyphens:none] lg:[word-break:normal] lg:overflow-visible"
            >
              <MaskedLine delay={0.2}>
                <span>
                  Administración y Gestión
                  <br className="hidden lg:block" />
                  de Comunidades
                </span>
              </MaskedLine>
              <MaskedLine delay={0.35}>
                <span className="section-subtitle block">con rigor ejecutivo y serenidad comunitaria.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: "easeOut" }}
              data-testid="hero-subtitle"
              className="mt-5 lg:mt-6 text-sm text-slate-600 sm:text-xl sm:text-slate-500 lg:text-lg leading-relaxed max-w-2xl lg:max-w-xl"
            >
              Cuidamos y revalorizamos el patrimonio de tu comunidad con información transparente, estricta seguridad jurídica y
              supervisión técnica presencial. Menos fricción vecinal, cuentas claras en tiempo real y cero improvisaciones.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0, ease: "easeOut" }}
              className="mt-6 lg:mt-8 flex flex-col sm:flex-row gap-4"
            >
              <a
                href="#contacto"
                data-testid="hero-primary-cta"
                className="inline-flex items-center justify-center bg-slate-900 text-white font-semibold text-base rounded-full px-9 py-3.5 sm:py-4 hover:bg-slate-800 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-slate-900/10 w-full sm:w-auto"
              >
                Solicitar Propuesta Personalizada
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 lg:mt-0 lg:self-start"
            data-testid="hero-image-frame"
          >
            <HeroCarousel />
          </motion.div>
        </div>

        <div className="mt-16 sm:mt-6 lg:mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="hero-value-cards">
          {valueCards.map(({ icon: Icon, title, text }, i) => (
            <motion.article
              key={title}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1 + i * 0.12, ease: "easeOut" }}
              className="group border border-slate-200 hover:border-slate-900 rounded-xl p-7 bg-white transition-colors duration-500"
            >
              <Icon className="w-5 h-5 text-slate-400 group-hover:text-slate-900 transition-colors duration-500 mb-8" />
              <h3 className="font-display card-title text-slate-900 leading-snug">{title}</h3>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">{text}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
